'use client';

import { useEffect, useRef } from 'react';
import Clarity from '@microsoft/clarity';
import { useAuth } from '@/lib/integrations/supabase';
import * as Sentry from '@sentry/nextjs';

export function ClarityProvider({ children }: { children: React.ReactNode }) {
  const { user, role, businessId, effectiveBusinessId, isAdmin } = useAuth();
  const initialized = useRef(false);
  const lastUserId = useRef<string | null>(null);
  const lastEffectiveBusinessId = useRef<string | null>(null);

  useEffect(() => {
    const projectId = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID;

    if (!projectId) {
      return;
    }

    if (initialized.current) {
      return;
    }

    try {
      Clarity.init(projectId);
      initialized.current = true;
    } catch (err) {
      Sentry.captureException(err);
    }
  }, []);

  useEffect(() => {
    if (!initialized.current) {
      return;
    }

    if (!user?.id) {
      return;
    }

    if (lastUserId.current === user.id) return;

    try {
      Clarity.identify(user.id);
      lastUserId.current = user.id;

      if (role) {
        Clarity.setTag('user_role', role);
      }

      if (businessId) {
        Clarity.setTag('business_id', businessId);
      }

      if (isAdmin) {
        Clarity.setTag('is_admin', 'true');
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [user?.id, role, businessId, isAdmin]);

  useEffect(() => {
    if (!initialized.current || !isAdmin) return;

    if (lastEffectiveBusinessId.current === effectiveBusinessId) return;

    try {
      Clarity.setTag('effective_business_id', effectiveBusinessId || 'none');
      lastEffectiveBusinessId.current = effectiveBusinessId;
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [effectiveBusinessId, isAdmin]);

  return <>{children}</>;
}
